/* Top Market — en-tête : barre collante, rail des rayons, méga-menu, tiroir mobile. */
(() => {
  if (customElements.get('tm-header')) return;

  const ORDI = window.matchMedia('(min-width: 990px)');
  const SURVOL = window.matchMedia('(hover: hover) and (pointer: fine)');
  const MOUVEMENT_REDUIT = window.matchMedia('(prefers-reduced-motion: reduce)');

  class TmHeader extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.section-header') || this.parentElement;
      this.racine = document.documentElement;
      this.nav = this.querySelector('.tm-nav');
      this.rail = this.querySelector('[data-tm-rail]');
      this.megas = Array.from(this.querySelectorAll('[data-tm-mega]'));
      this.panneaux = [this.rail, ...this.megas].filter(Boolean);
      this.tiroir = this.section.querySelector('.tm-tiroir');
      this.minuteries = new WeakMap();
      this.derniereOrdonnee = Math.max(0, window.scrollY);
      this.abandon = new AbortController();
      const options = { signal: this.abandon.signal };

      this.observateur = new ResizeObserver(() => this.mesurer());
      this.observateur.observe(this);
      ORDI.addEventListener('change', () => {
        this.fermerPanneaux();
        this.fermerTiroir(true);
        this.section.classList.remove('tm-cache');
        this.mesurer();
      }, options);
      window.addEventListener('scroll', () => this.auDefilement(), { passive: true, signal: this.abandon.signal });

      this.preparerPanneaux(options);
      this.preparerTiroir(options);
      this.preparerEditeur(options);
      this.mesurer();
      this.auDefilement();
    }

    disconnectedCallback() {
      this.abandon.abort();
      this.observateur.disconnect();
    }

    /* --- Barre collante --- */

    // Sur ordinateur, la section se décale vers le haut de la hauteur des deux premières rangées :
    // seule la rangée de navigation reste visible une fois la page défilée.
    mesurer() {
      const ordi = ORDI.matches;
      const collant = ordi ? this.dataset.collantOrdi === 'true' : this.dataset.collantMobile === 'true';
      const decalage = ordi && collant && this.nav ? this.nav.offsetTop : 0;
      this.section.classList.toggle('tm-collant', collant);
      this.section.style.setProperty('--tm-decalage-collant', `${decalage}px`);
      this.racine.style.setProperty('--header-height', `${this.offsetHeight - decalage}px`);
    }

    // Sur mobile, l'en-tête s'efface quand on descend et revient dès qu'on remonte.
    auDefilement() {
      if (this.trameDemandee) return;
      this.trameDemandee = true;
      requestAnimationFrame(() => {
        this.trameDemandee = false;
        const y = Math.max(0, window.scrollY);
        const ecart = y - this.derniereOrdonnee;
        this.section.classList.toggle('tm-defile', y > 8);
        const masquable = !ORDI.matches && this.dataset.collantMobile === 'true';
        if (!masquable) {
          this.section.classList.remove('tm-cache');
        } else if (Math.abs(ecart) > 6) {
          const rechercheOuverte = this.querySelector('predictive-search[open]');
          const tiroirOuvert = this.tiroir && this.tiroir.open;
          if (ecart > 0 && y > this.offsetHeight && !rechercheOuverte && !tiroirOuvert) {
            this.section.classList.add('tm-cache');
          } else if (ecart < 0) {
            this.section.classList.remove('tm-cache');
          }
        }
        if (Math.abs(ecart) > 6) this.derniereOrdonnee = y;
      });
    }

    /* --- Rail des rayons et méga-menu --- */

    zoneDe(details) {
      return details === this.rail ? details.parentElement : details.closest('.tm-barre__item');
    }

    survolActif(evenement) {
      return ORDI.matches && SURVOL.matches && evenement.pointerType === 'mouse';
    }

    // Sur l'accueil, le bandeau affiche déjà la liste des rayons sous le bloc : inutile de l'ouvrir par-dessus.
    railStatiqueVisible() {
      const statique = document.querySelector('.tm-vedette__rail');
      if (!statique || !ORDI.matches) return false;
      const cadre = statique.getBoundingClientRect();
      return cadre.height > 0 && cadre.bottom > this.getBoundingClientRect().bottom + 40;
    }

    programmer(details, ouvrir, delai) {
      clearTimeout(this.minuteries.get(details));
      this.minuteries.set(details, setTimeout(() => {
        if (ouvrir) {
          if (!details.open) {
            details.tmOuvertAuSurvol = performance.now();
            details.open = true;
          }
        } else if (details.open && !details.contains(document.activeElement)) {
          details.open = false;
        }
      }, delai));
    }

    fermerPanneaux() {
      this.panneaux.forEach((details) => {
        if (details.open) details.open = false;
      });
    }

    preparerPanneaux(options) {
      this.panneaux.forEach((details) => {
        const sommaire = details.querySelector('summary');
        const zone = this.zoneDe(details);
        sommaire.setAttribute('aria-expanded', String(details.open));

        details.addEventListener('toggle', () => {
          sommaire.setAttribute('aria-expanded', String(details.open));
          if (details.open) {
            this.panneaux.forEach((autre) => {
              if (autre !== details) autre.open = false;
            });
          }
        }, options);

        sommaire.addEventListener('click', (evenement) => {
          // Sur mobile, le tiroir remplace les panneaux.
          if (!ORDI.matches) {
            evenement.preventDefault();
            // Dans la barre mobile, une entrée du menu mène à sa page ; le détail de ses sous-liens reste dans le tiroir.
            if (details !== this.rail && details.dataset.tmUrl) {
              window.location.assign(details.dataset.tmUrl);
              return;
            }
            this.ouvrirTiroir(sommaire, details === this.rail ? 'rayons' : details.dataset.tmEntree);
            return;
          }
          if (details === this.rail && this.railStatiqueVisible()) {
            evenement.preventDefault();
            const premier = document.querySelector('.tm-vedette__rail a');
            if (premier) premier.focus();
            return;
          }
          // Un survol vient d'ouvrir le panneau : le clic qui suit ne doit pas le refermer.
          if (details.open && performance.now() - (details.tmOuvertAuSurvol || 0) < 450) evenement.preventDefault();
        }, options);

        zone.addEventListener('pointerenter', (evenement) => {
          if (!this.survolActif(evenement)) return;
          if (details === this.rail && this.railStatiqueVisible()) return;
          this.programmer(details, true, 120);
        }, options);

        zone.addEventListener('pointerleave', (evenement) => {
          if (!this.survolActif(evenement)) return;
          this.programmer(details, false, 220);
        }, options);
      });

      this.addEventListener('keydown', (evenement) => {
        if (evenement.key !== 'Escape') return;
        const ouvert = this.panneaux.find((details) => details.open);
        if (!ouvert) return;
        ouvert.open = false;
        ouvert.querySelector('summary').focus();
      }, options);

      this.addEventListener('focusout', () => {
        requestAnimationFrame(() => {
          this.panneaux.forEach((details) => {
            const zone = this.zoneDe(details);
            if (details.open && !zone.contains(document.activeElement) && !zone.matches(':hover')) details.open = false;
          });
        });
      }, options);

      document.addEventListener('click', (evenement) => {
        if (this.nav && this.nav.contains(evenement.target)) return;
        this.fermerPanneaux();
      }, options);
    }

    /* --- Tiroir de menu (mobile) --- */

    preparerTiroir(options) {
      this.querySelectorAll('[data-tm-ouvrir-tiroir]').forEach((bouton) => {
        bouton.addEventListener('click', (evenement) => {
          evenement.preventDefault();
          this.ouvrirTiroir(bouton);
        }, options);
        bouton.addEventListener('keydown', (evenement) => {
          if (evenement.key !== ' ') return;
          evenement.preventDefault();
          this.ouvrirTiroir(bouton);
        }, options);
      });
      if (!this.tiroir) return;
      this.tiroir.addEventListener('click', (evenement) => {
        if (evenement.target === this.tiroir || evenement.target.closest('[data-tm-fermer-tiroir]')) this.fermerTiroir();
      }, options);
      this.tiroir.addEventListener('cancel', (evenement) => {
        evenement.preventDefault();
        this.fermerTiroir();
      }, options);
    }

    ouvrirTiroir(declencheur, cible) {
      // Sans tiroir utilisable, le bouton mène à la page de toutes les collections.
      if (!this.tiroir || typeof this.tiroir.showModal !== 'function') {
        const repli = this.querySelector('[data-tm-ouvrir-tiroir]');
        window.location.href = repli ? repli.getAttribute('href') : '/collections';
        return;
      }
      if (this.tiroir.open) return;
      this.declencheur = declencheur;
      this.tiroir.showModal();
      this.racine.classList.add('tm-verrou');
      this.querySelectorAll('[data-tm-ouvrir-tiroir]').forEach((bouton) => bouton.setAttribute('aria-expanded', 'true'));
      requestAnimationFrame(() => this.tiroir.classList.add('tm-ouvert'));
      if (cible) {
        const groupe = cible === 'rayons'
          ? this.tiroir.querySelector('[data-tm-tiroir-rayons]')
          : this.tiroir.querySelector(`[data-tm-entree="${cible}"]`);
        if (groupe) {
          if (groupe.tagName === 'DETAILS') groupe.open = true;
          groupe.scrollIntoView({ block: 'start' });
        }
      }
    }

    fermerTiroir(immediat = false) {
      if (!this.tiroir || !this.tiroir.open) return;
      let fini = false;
      const finir = () => {
        if (fini) return;
        fini = true;
        this.tiroir.close();
        this.racine.classList.remove('tm-verrou');
        this.querySelectorAll('[data-tm-ouvrir-tiroir]').forEach((bouton) => bouton.setAttribute('aria-expanded', 'false'));
        if (this.declencheur && document.contains(this.declencheur)) this.declencheur.focus();
      };
      this.tiroir.classList.remove('tm-ouvert');
      if (immediat || MOUVEMENT_REDUIT.matches) {
        finir();
        return;
      }
      this.tiroir.addEventListener('transitionend', finir, { once: true });
      setTimeout(finir, 320);
    }

    /* --- Éditeur de thème : montrer le panneau du bloc sélectionné --- */

    preparerEditeur(options) {
      if (!(window.Shopify && window.Shopify.designMode)) return;
      this.addEventListener('shopify:block:select', (evenement) => {
        const details = evenement.target.closest('[data-tm-mega]');
        if (details && ORDI.matches) details.open = true;
      }, options);
      this.addEventListener('shopify:block:deselect', (evenement) => {
        const details = evenement.target.closest('[data-tm-mega]');
        if (details) details.open = false;
      }, options);
    }
  }

  customElements.define('tm-header', TmHeader);
})();
